import { createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { userLogin, userLogout, addUser, importUsers, changePassword } from './authThunk';

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    token: null,
    refreshToken: null,
    permissions: [],
    loading: false,
    error: null,
    isAuthenticated: false,
    importProgress: 0,
    // Password change states
    passwordChangeLoading: false,
    passwordChangeError: null,
    isPasswordChanged: false,
  },
  reducers: {
    // Add a reducer to manually set auth state (useful for app initialization)
    setAuthState: (state, action) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.permissions = action.payload.response.data[0].permissions || [];
      state.isAuthenticated = !!action.payload.token;
    },
    // Clear auth state (useful for token expiration)
    clearAuthState: (state) => {
      state.user = null;
      state.token = null;
      state.permissions = [];
      state.isAuthenticated = false;
      state.passwordChangeLoading = false;
      state.passwordChangeError = null;
      state.isPasswordChanged = false;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('permissions');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(userLogin.pending, (state) => {
        state.loading = true;
        state.error = null; // Clear previous errors
      })
      .addCase(userLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.permissions = action.payload.response.data[0].permissions || [];
        state.isAuthenticated = true;
        state.error = null;

        console.log('action=============', action);

        // Store in localStorage
        if (action.payload.token) {
          localStorage.setItem('token', action.payload.token);
        }
        if (action.payload.refreshToken) {
          localStorage.setItem('refreshToken', action.payload.refreshToken);
        }
        if (action.payload.user) {
          localStorage.setItem('user', JSON.stringify(action.payload.user));
        }
        if (action.payload.response.data[0].permissions) {
          localStorage.setItem(
            'permissions',
            JSON.stringify(action.payload.response.data[0].permissions),
          );
        }
      })
      .addCase(userLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload || 'Login failed';
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      })
      .addCase(userLogout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(userLogout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.permissions = [];
        state.isAuthenticated = false;
        state.loading = false;
        state.error = null;
        state.isPasswordChanged = false;
        state.passwordChangeError = null;
        // localStorage cleanup is also done in thunk, but do it here too for safety
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('permissions');
      })
      .addCase(userLogout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload || 'Logout failed';
        // Even if logout API fails, clear local state
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      })

      .addCase(addUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addUser.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'User Registration successfully';
      })
      .addCase(addUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.Payload;
      })

      .addCase(importUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.importProgress = 0;
      })
      .addCase(importUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message || 'Users imported successfully';
        state.error = null;
        state.importProgress = 100;
      })
      .addCase(importUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to import users';
        state.importProgress = 0;
      })
      // ===== CHANGE PASSWORD =====
      .addCase(changePassword.pending, (state) => {
        state.passwordChangeLoading = true;
        state.passwordChangeError = null;
        state.isPasswordChanged = false;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.passwordChangeLoading = false;
        state.isPasswordChanged = true;
        state.passwordChangeError = null;
        state.success = 'Password changed successfully';
        // toast.success('Password changed successfully! Please login again');
        // Auto logout after password change
        // Clear all auth state
        state.user = null;
        state.token = null;
        state.permissions = [];
        state.isAuthenticated = false;
        state.loading = false;
        state.error = null;

        // Clear localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('permissions');
        sessionStorage.clear();
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.passwordChangeLoading = false;
        state.isPasswordChanged = false;
        state.passwordChangeError = action.payload || 'Failed to change password.';
        toast.error(state.passwordChangeError);
      });
  },
});

// Export actions
export const { setAuthState, clearAuthState } = authSlice.actions;

export default authSlice.reducer;
